import { getAuth } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { IInventoryReportType } from '../sp-types/InventoryReportType';
import { db } from './firebase';
import { StatusType } from '../sp-types/statusType';
import { deleteItem, getItems, saveItem, updateItem } from './genericFB';
import { MonthYear } from '../utils/MonthYear/monthYear';

const INVENTORY_REPORTS_COLLECTION_NAME = 'InventoryReports';
const DB = db;

const getReportsOfMonth = async (
  monthYear: MonthYear,
  materialId: string,
  accountId: string,
): Promise<IInventoryReportType[]> => {
  const items = await getItems<IInventoryReportType>(
    accountId,
    INVENTORY_REPORTS_COLLECTION_NAME,
    (q) =>
      query(
        q,
        where('monthYear', '==', monthYear),
        where('materialId', '==', materialId),
        where('status', '==', 'completed'),
      ),
    true,
  );

  return items?.sort((a, b) => a.reportingTime.seconds - b.reportingTime.seconds) || [];
};

export async function saveInventoryReport(
  report: IInventoryReportType,
  isAdd: boolean,
): Promise<void | { error: any }> {
  try {
    if (isAdd) {
      const docRef = await addDoc(collection(DB, INVENTORY_REPORTS_COLLECTION_NAME), report);

      console.log('Doc created ID: ', docRef.id);
    } else {
      const updated = await saveItem<IInventoryReportType>(report.id || '', report, INVENTORY_REPORTS_COLLECTION_NAME);

      console.log('updated item', updated?.id);
    }
  } catch (error) {
    console.log(`Error found! ${error}`);
    return { error };
  }
}

export async function setInventoryReportById(documentId: string, data: IInventoryReportType): Promise<boolean> {
  try {
    const docRef = doc(DB, INVENTORY_REPORTS_COLLECTION_NAME, documentId);
    await setDoc(docRef, data, { merge: true });
    console.log(`Document with ID ${documentId} successfully set.`);
    return true;
  } catch (error) {
    console.error('Error setting document:', error);
    return false;
  }
}

export async function getAllInventoryReportOfAccount(
  accountId: string,
  statusTypes: StatusType[],
): Promise<IInventoryReportType[] | null> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.error('User is not authenticated');
    return null;
  }

  const inventoryReportTypesRef = collection(DB, INVENTORY_REPORTS_COLLECTION_NAME);

  try {
    const querySnapshot = await getDocs(
      query(
        inventoryReportTypesRef,
        where('accountId', '==', accountId),
        where('status', 'in', statusTypes),
        orderBy('reportingTime', 'desc'),
      ),
    );

    return querySnapshot.docs?.map((doc) => ({ id: doc.id, ...(doc.data() as IInventoryReportType) }));
  } catch (error) {
    console.error('Error getting documents:', error);
    return null;
  }
}

export const deleteInventoryReports = async (accountId: string) => {
  const inventoryReportsRef = collection(DB, INVENTORY_REPORTS_COLLECTION_NAME);
  const q = query(inventoryReportsRef, where('accountId', '==', accountId));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    console.log('No matching documents.');
    return;
  }
  for (const aDoc of querySnapshot.docs) {
    await deleteDoc(aDoc.ref);
  }

  console.log(`Deleted ${querySnapshot.size} documents where accountId == ${accountId}`);
};

export async function deleteInventoryReport(
  accountId: string,
  materialId: string,
  quantity: number,
  reportingTime: Timestamp,
): Promise<boolean> {
  const inventoryReportsRef = collection(DB, INVENTORY_REPORTS_COLLECTION_NAME);
  const q = query(
    inventoryReportsRef,
    where('accountId', '==', accountId),
    where('materialId', '==', materialId),
    where('quantity', '==', quantity),
    where('reportingTime', '==', reportingTime),
  );

  try {
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      console.log('No matching documents found.');
      return false;
    }
    for (const doc of querySnapshot.docs) {
      await deleteDoc(doc.ref);
    }
    return true;
  } catch (error) {
    console.error('Error deleting document:', error);
    return false;
  }
}

export async function deleteInventoryReportId(reportId: string): Promise<undefined> {
  return deleteItem(reportId, INVENTORY_REPORTS_COLLECTION_NAME);
}
export async function updateInventoryReport(
  reportId: string,
  updatedData: Partial<IInventoryReportType>,
): Promise<IInventoryReportType | null> {
  return updateItem<IInventoryReportType>(reportId, updatedData, INVENTORY_REPORTS_COLLECTION_NAME);
}
